import type { PortalConfig } from './config';

/**
 * Cognito hosted UI login using the authorization code flow with PKCE.
 *
 * PKCE rather than the implicit flow means no client secret has to exist and no
 * token is ever placed in a URL. Tokens are held in sessionStorage so they do not
 * outlive the browser tab.
 */

const VERIFIER_KEY = 'lighthouse.pkce.verifier';
const STATE_KEY = 'lighthouse.pkce.state';
const TOKENS_KEY = 'lighthouse.tokens';

interface StoredTokens {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  /** Epoch milliseconds. */
  expiresAt: number;
}

export interface Session {
  idToken: string;
  email: string;
  username: string;
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(byteLength = 48): string {
  return base64UrlEncode(crypto.getRandomValues(new Uint8Array(byteLength)));
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

function readTokens(): StoredTokens | undefined {
  const raw = sessionStorage.getItem(TOKENS_KEY);
  if (!raw) return undefined;

  try {
    return JSON.parse(raw) as StoredTokens;
  } catch {
    sessionStorage.removeItem(TOKENS_KEY);
    return undefined;
  }
}

function storeTokens(tokens: StoredTokens): void {
  sessionStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
}

interface IdTokenClaims {
  email?: string;
  'cognito:username'?: string;
  exp?: number;
}

/**
 * Reads display claims only. The token's signature is verified by the API
 * Gateway JWT authorizer, so nothing here is trusted for authorisation.
 */
function decodeClaims(idToken: string): IdTokenClaims {
  const payload = idToken.split('.')[1];
  if (!payload) return {};

  try {
    // JWTs use base64url with the padding stripped, which atob will not accept.
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '=');
    const bytes = Uint8Array.from(atob(base64), (character) => character.charCodeAt(0));

    return JSON.parse(new TextDecoder().decode(bytes)) as IdTokenClaims;
  } catch {
    return {};
  }
}

async function redirectToLogin(config: PortalConfig): Promise<never> {
  const verifier = randomString();
  const state = randomString(16);

  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.userPoolClientId,
    redirect_uri: config.redirectUri,
    scope: 'openid email profile',
    state,
    code_challenge: await challengeFor(verifier),
    code_challenge_method: 'S256',
  });

  // Everyone signs in with their SailPoint account, so naming the provider sends
  // the browser to Entra ID directly instead of to a Cognito page whose only
  // content is one button.
  if (config.identityProvider) {
    params.set('identity_provider', config.identityProvider);
  }

  window.location.assign(`https://${config.hostedUiDomain}/oauth2/authorize?${params}`);

  // The assignment above navigates away; this only satisfies the return type.
  return new Promise<never>(() => {});
}

async function requestTokens(config: PortalConfig, body: URLSearchParams): Promise<StoredTokens> {
  const response = await fetch(`https://${config.hostedUiDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body,
  });

  if (!response.ok) {
    throw new Error(`Token request failed (HTTP ${response.status}).`);
  }

  const payload = (await response.json()) as {
    id_token: string;
    access_token: string;
    refresh_token?: string;
    expires_in: number;
  };

  return {
    idToken: payload.id_token,
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token,
    expiresAt: Date.now() + payload.expires_in * 1000,
  };
}

/**
 * Completes login if we have just come back from Cognito, reuses a valid session
 * if there is one, refreshes a stale one, and otherwise starts a fresh login.
 */
export async function establishSession(config: PortalConfig): Promise<Session> {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');
  const state = url.searchParams.get('state');
  const error = url.searchParams.get('error');

  if (error) {
    throw new Error(`Cognito rejected the login: ${url.searchParams.get('error_description') ?? error}`);
  }

  if (code) {
    const expectedState = sessionStorage.getItem(STATE_KEY);
    const verifier = sessionStorage.getItem(VERIFIER_KEY);

    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(VERIFIER_KEY);

    // Guards against an authorization response being replayed into this tab.
    if (!verifier || !expectedState || state !== expectedState) {
      return redirectToLogin(config);
    }

    const tokens = await requestTokens(
      config,
      new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: config.userPoolClientId,
        code,
        redirect_uri: config.redirectUri,
        code_verifier: verifier,
      }),
    );

    storeTokens(tokens);

    // Keep the code out of history and out of anything that logs URLs.
    window.history.replaceState({}, '', url.pathname);

    return toSession(tokens);
  }

  const existing = readTokens();

  if (existing && existing.expiresAt > Date.now() + 60_000) {
    return toSession(existing);
  }

  if (existing?.refreshToken) {
    try {
      const refreshed = await requestTokens(
        config,
        new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: config.userPoolClientId,
          refresh_token: existing.refreshToken,
        }),
      );

      // Cognito omits the refresh token on refresh, so carry the old one forward.
      const tokens = { ...refreshed, refreshToken: refreshed.refreshToken ?? existing.refreshToken };
      storeTokens(tokens);
      return toSession(tokens);
    } catch {
      sessionStorage.removeItem(TOKENS_KEY);
    }
  }

  return redirectToLogin(config);
}

function toSession(tokens: StoredTokens): Session {
  const claims = decodeClaims(tokens.idToken);

  return {
    idToken: tokens.idToken,
    email: claims.email ?? 'unknown',
    username: claims['cognito:username'] ?? claims.email ?? 'unknown',
  };
}

export function currentIdToken(): string | undefined {
  return readTokens()?.idToken;
}

/**
 * Clears the local session and the Cognito one. It does not end the Entra ID
 * session, so signing back in will not prompt for credentials again — that is
 * single sign-on working as intended, not a bug. Closing the browser or signing out
 * of Microsoft is what ends it.
 */
export function signOut(config: PortalConfig): void {
  sessionStorage.removeItem(TOKENS_KEY);

  const params = new URLSearchParams({
    client_id: config.userPoolClientId,
    logout_uri: config.redirectUri,
  });

  window.location.assign(`https://${config.hostedUiDomain}/logout?${params}`);
}
